import React from 'react';
import GradientModal from './GradientModal';

/**
 * Reusable modal for selecting custom date ranges.
 * Used by HarvestLogger, PlantingSchedule, and potentially other pages with date filtering.
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {string} props.itemLabel - Label for what's being filtered (e.g., "harvests", "plantings")
 * @param {Object} props.dateRange - { start: string, end: string } in YYYY-MM-DD format
 * @param {Function} props.onDateRangeChange - Handler to update date range
 * @param {Function} props.onApply - Handler called when Apply is clicked (with valid dates)
 * @param {Function} props.onClose - Handler to close the modal
 */
const CustomDateRangeModal = ({
  isOpen,
  itemLabel = 'items',
  dateRange,
  onDateRangeChange,
  onApply,
  onClose
}) => {
  if (!isOpen) return null;

  const handleApply = () => {
    if (dateRange.start && dateRange.end) {
      onApply();
    }
    onClose();
  };

  return (
    <GradientModal 
      isOpen={true} 
      onClose={onClose} 
      title="Custom Date Range" 
      maxWidth="max-w-sm"
      primaryAction={{ 
        label: 'Apply', 
        onClick: handleApply, 
        disabled: !dateRange.start || !dateRange.end 
      }}
      secondaryAction={{ label: 'Cancel', onClick: onClose }}
    >
      <p className="text-gray-600 text-sm mb-3 sm:mb-4">
        Select a date range to filter {itemLabel}:
      </p>
      
      <div className="space-y-4 mb-4 sm:mb-6">
        <div>
          <label className="block text-sm font-semibold text-[#2F5233] mb-2">Start Date</label>
          <div className="overflow-hidden">
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => onDateRangeChange(prev => ({ ...prev, start: e.target.value }))}
              className="w-full min-w-0 max-w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] appearance-none bg-white"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-[#2F5233] mb-2">End Date</label>
          <div className="overflow-hidden">
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => onDateRangeChange(prev => ({ ...prev, end: e.target.value }))}
              className="w-full min-w-0 max-w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] appearance-none bg-white"
            />
          </div>
        </div>
      </div>
    </GradientModal>
  );
};

export default CustomDateRangeModal;
